import { Component, OnInit, ViewEncapsulation, AfterViewInit, Input, OnDestroy } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Subject } from 'rxjs';
import { take, takeUntil, switchMap } from 'rxjs/operators';
import { Store } from '@ngrx/store';

import { Helpers } from '../../../helpers';
import { AuthenticationService } from '../../../auth/_services';
import { User } from '../../pages/default/plex/users/user.model';
import { EntitiesService } from '../../pages/default/plex/entities/entities.service';
import { SchemesService } from '../../pages/default/management/entities/entities.service';
import { Entity } from '../../pages/default/plex/entities/entities.model';
import { environment } from '../../../../environments/environment';
import { PopOverService } from '../../../_services/popovers.service';
import { ManagementService } from '../../pages/default/management/management.service';
import { config } from '../../../../../src/configs/configs';
import { TasksService } from '../../pages/default/plex/tasks/tasks.service';
import { selectEntityId } from 'src/app/_state/entity/entity.selectors';
import { UsersService } from '../../pages/default/plex/users/users.service';
import { TypesenseService } from '../../../_shared/typesense/typesense.service';

interface TaskAlert {
	id: string;
	entityId: string;
	refNo: string;
	subject: string;
	dueDate: Date;
	status: string;
	isOverdue: boolean;
	taskType: string;
}

declare const mLayout: any;

@Component({
	selector: 'app-header-nav',
	templateUrl: './header-nav.component.html',
	encapsulation: ViewEncapsulation.None,
})
export class HeaderNavComponent implements OnInit, AfterViewInit, OnDestroy {
	config = config[environment.product];
	user: User = {
		firstname: '',
		surname: '',
		email: '',
	};
	userDetails;
	userUid;
	entities: Entity[];
	showHeaderMenu: boolean = false;
	showPopover: boolean;
	@Input() entity: Entity = { name: '' };
	@Input() entityId;

	showHelp: any;
	projectEntity: Entity;
	envType = environment.product;

	// for WHITFIELDS activities
	public myTaskCount = 0;
	public userActivities: any;
	public loadingNotificationTasks: boolean;
	private showingNotification = false;

	taskAlerts: TaskAlert[] = [];
	taskAlertCount = 0;
	overdueTaskCount = 0;
	upcomingTaskCount = 0;
	refreshingTaskAlerts = false;
	private taskAlertsSubscribed = false;
	private destroy$ = new Subject<void>();

	constructor(
		private tasksService: TasksService,
		private auth: AuthenticationService,
		private router: Router,
		public popOverService: PopOverService,
		private entitiesService: EntitiesService,
		private managementService: ManagementService,
		private store: Store,
		private schemesService: SchemesService,
		private usersService: UsersService,
		private typesenseService: TypesenseService
	) {}
	ngOnInit() {
		this.store
			.select(selectEntityId)
			.pipe(takeUntil(this.destroy$))
			.subscribe(entityId => (this.entityId = entityId));
		this.auth.user.pipe(takeUntil(this.destroy$)).subscribe(userDetails => {
			if (!userDetails) return;
			this.user = userDetails;
			this.userUid = userDetails.uid ? userDetails.uid : '';
			this.fetchUserDetails();

			if (this.userUid && !this.taskAlertsSubscribed) {
				this.subscribeToTaskAlerts();
			}
		});
		this.showHeaderMenu = false;
		const urlChecker = () => {
			if (this.entityId !== 'whitfields' && this.entityId !== 'plex' && this.entityId !== 'serve') {
				this.showHeaderMenu = true;
			} else {
				this.showHeaderMenu = false;
			}
		};
		urlChecker();

		this.managementService
			.fetchProjectEntity()
			.pipe(takeUntil(this.destroy$))
			.subscribe((entityData: Entity) => {
				this.projectEntity = entityData;
			});

		this.router.events.pipe(takeUntil(this.destroy$)).subscribe((e: any) => {
			if (e instanceof NavigationEnd) {
				urlChecker();
			}
		});

		this.entitiesService
			.getUsersEntities()
			.pipe(takeUntil(this.destroy$))
			.subscribe(entitiesData => {
				this.entities = entitiesData;
			});

		//fetch task MY TASK count for this entitiy
		if (this.showHeaderMenu) {
			let lastReadDate: Date;
			this.tasksService
				.getUserWhitfieldsLastReadDate()
				.pipe(
					takeUntil(this.destroy$),
					switchMap((lastRead: any) => {
						if (lastRead) {
							this.myTaskCount = 0;
							lastReadDate = new Date(lastRead.date);
							return this.tasksService.getUserWhitfieldsTasks().pipe(takeUntil(this.destroy$));
						}
						return [];
					})
				)
				.subscribe((tasks: any) => {
					if (tasks && lastReadDate) {
						this.userActivities = tasks;
						this.myTaskCount = 0;

						tasks.forEach((activity: any) => {
							const actDate = new Date(parseInt(`${activity.created.seconds}${activity.created.nanoseconds}`) / 1000000);

							if (actDate > lastReadDate) {
								this.myTaskCount++;
							}
						});
					}
				});
		}
	}
	fetchUserDetails() {
		this.usersService
			.fetchUserDetails(`/users/${this.userUid}`)
			.pipe(take(1))
			.subscribe((userData: User) => {
				this.userDetails = userData;
			});
	}

	clickedNotification() {
		this.showingNotification = !this.showingNotification;

		// if (!this.showingNotification) {
		// 	this.tasksService.updateUserWhitfieldsLastReadDate();
		// }
	}

	navigateToTab(tabName) {
		this.router.navigate([`/${this.entityId}${this.config.entityEditUrl}`], { queryParams: { tab: tabName } });
	}

	checkTasks() {
		this.entitiesService.getTaskCounts().subscribe(taskcounts => {
			this.myTaskCount = taskcounts.length;
		});
	}

	public async setEntityData(userEntity: Entity): Promise<void> {
		Helpers.setLoading(true);

		await this.entitiesService.forceEntityStateUpdate(userEntity.id);

		this.entitiesService
			.getEntityDetails(userEntity.id)
			.pipe(take(1))
			.subscribe(async entity => {
				await this.schemesService.setEntity({ ...entity, ...userEntity });
				const entityId = this.entityId;
				await this.router.navigate([this.config.entitiesUrl]); // Force dashboard to detatch.
				await this.router.navigate([`/${entityId}${this.config.dashboardUrl}`]);

				Helpers.setLoading(false);
			});
	}

	navigate() {
		this.router.navigate([`${this.config.entitiesUrl}`]);
	}

	ngAfterViewInit() {
		mLayout.initHeader();
	}

	navToClient() {
		window.open(`${this.entity.clientWebsiteUrl}`, '_blank');
	}

	navToWebsite(url) {
		window.open(`${url}`, '_blank');
	}

	toggleSidebar() {
		this.popOverService.toggleHelpVisibility();
	}

	async subscribeToTaskAlerts() {
		const userId = this.userUid;
		if (!userId) return;

		this.taskAlertsSubscribed = true;

		const excludedStatuses = ['Completed', 'Saved', 'Resolved', 'Rejected', 'Paid'];
		const statusFilter = excludedStatuses.map(s => `status:!=${s}`).join(' && ');
		const baseFilter = `product:whitfields && ${statusFilter}`;

		try {
			const [managerResults, teamResults, creatorResults] = await Promise.all([
				this.typesenseService.searchTasks('*', `${baseFilter} && managerId:${userId}`, 10, 1, { field: 'created', order: 'desc' }),
				this.typesenseService.searchTasks('*', `${baseFilter} && taskTeam:${userId}`, 10, 1, { field: 'created', order: 'desc' }),
				this.typesenseService.searchTasks('*', `${baseFilter} && creatorId:${userId}`, 10, 1, { field: 'created', order: 'desc' }),
			]);

			const now = new Date();
			const dueSoonThreshold = new Date(now.getTime() + 2 * 24 * 60 * 60 * 1000);
			const taskMap = new Map<string, TaskAlert>();

			const processHits = (hits: any[]) => {
				for (const hit of hits) {
					const data = hit.document;
					if (taskMap.has(data.id)) continue;
					if (!data.dueDate) continue;

					const dueDate = new Date(typeof data.dueDate === 'number' ? (data.dueDate > 1e12 ? data.dueDate : data.dueDate * 1000) : data.dueDate);
					if (isNaN(dueDate.getTime()) || dueDate > dueSoonThreshold) continue;
					taskMap.set(data.id, {
						id: data.id,
						entityId: data.entityId || '',
						refNo: data.refNo || '',
						subject: data.subject || data.taskCategory || `${data.taskType} Task`,
						dueDate,
						status: data.status || '',
						isOverdue: dueDate < now,
						taskType: data.taskType || 'General',
					});
				}
			};

			processHits(managerResults.hits || []);
			processHits(teamResults.hits || []);
			processHits(creatorResults.hits || []);

			this.taskAlerts = Array.from(taskMap.values()).sort((a, b) => a.dueDate.getTime() - b.dueDate.getTime());
			this.taskAlertCount = this.taskAlerts.length;

			const in24Hours = new Date(now.getTime() + 24 * 60 * 60 * 1000);
			this.overdueTaskCount = this.taskAlerts.filter(t => t.isOverdue).length;
			this.upcomingTaskCount = this.taskAlerts.filter(t => !t.isOverdue && t.dueDate <= in24Hours).length;
		} catch (err) {
			console.error('Error fetching task alerts from Typesense:', err);
		}
	}

	async refreshTaskAlerts() {
		this.refreshingTaskAlerts = true;
		this.taskAlertsSubscribed = false;
		await this.subscribeToTaskAlerts();
		this.refreshingTaskAlerts = false;
	}

	goToTask(alert: TaskAlert) {
		const url = this.router.serializeUrl(this.router.createUrlTree([`/whitfields/management/tasks/edit/${alert.entityId}/General/${alert.id}`]));
		window.open(url, '_blank');
	}

	viewAllTaskAlerts() {
		this.router.navigate(['/whitfields/management/tasks/dashboard']);
	}

	getDueLabel(alert: TaskAlert): string {
		const now = new Date();
		const diffMs = alert.dueDate.getTime() - now.getTime();
		const diffHours = Math.round(diffMs / (1000 * 60 * 60));

		if (diffHours < -24) {
			const days = Math.abs(Math.round(diffHours / 24));
			return `${days} day${days > 1 ? 's' : ''} overdue`;
		} else if (diffHours < 0) {
			return `${Math.abs(diffHours)}h overdue`;
		} else if (diffHours < 24) {
			return `Due in ${diffHours}h`;
		} else {
			const days = Math.round(diffHours / 24);
			return `Due in ${days} day${days > 1 ? 's' : ''}`;
		}
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}

	logout() {
		this.auth.signOut();
	}

	async navigateToScheme(url: string) {
		await this.router.navigate([this.config.entitiesUrl]); // Force dashboard to detatch.
		this.router.navigate([`${url}`]);
	}
}
